import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, CheckCircle2, Clock, IdCard, Mail, XCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { VERIFICATION_TYPES, safeReturnPath, stepLink, verificationStatus } from '@/utils/verifications';
import { BACKEND_URL } from '@/config';
import PhoneCard from './PhoneCard';

const API = BACKEND_URL + '/api';

const STATE = {
  VERIFIED: { label: 'Vérifié', icon: CheckCircle2, cls: 'completed' },
  PENDING: { label: 'En cours d’examen', icon: Clock, cls: 'pending' },
  REJECTED: { label: 'Refusé', icon: XCircle, cls: 'cancelled' },
  NONE: { label: 'À faire', icon: Clock, cls: 'draft' },
};

function Badge({ state }) {
  const s = STATE[state] || STATE.NONE;
  const Icon = s.icon;
  return <span className={`status status-${s.cls}`}><Icon size={12} aria-hidden="true" /> {s.label}</span>;
}

/**
 * Mes vérifications (/app/verifications) — e-mail, téléphone, pièce d'identité, entreprise (décisions du 2026-10-17).
 * L'état vient du serveur (`user.verifications`) ; l'adresse e-mail et le téléphone (`PhoneCard`) se vérifient ici
 * avec le code reçu.
 */
export default function VerificationsPage() {
  const { user, checkAuth } = useAuth();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');
  const types = user?.verifications?.types || {};
  const email = verificationStatus(user, 'email');
  // Venu d'une action verrouillée (?retour=) : on y ramène, et la page d'identité aussi
  const [params] = useSearchParams();
  const back = safeReturnPath(params.get('retour'));

  const sendCode = async () => {
    setBusy('send');
    try {
      const { data } = await axios.post(`${API}/verifications/email/send`);
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Le code n’a pas pu être envoyé. Réessayez.');
    } finally { setBusy(''); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy('confirm');
    try {
      await axios.post(`${API}/verifications/email/confirm`, { code: code.trim() });
      toast.success('Adresse e-mail vérifiée.');
      setCode('');
      await checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code incorrect.');
    } finally { setBusy(''); }
  };

  return (
    <div>
      <div className="c2s-topbar">
        <div className="c2s-topbar-title">Mes vérifications</div>
        <div className="c2s-topbar-sub">Elles protègent les membres : consulter est libre, publier, proposer ou contacter les exige.</div>
      </div>

      {back && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', textAlign: 'left', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} data-testid="verif-return">
          <div style={{ flex: 1, fontSize: 13, color: 'var(--ink2)' }}>Une fois l’étape faite, reprenez où vous en étiez.</div>
          <Link to={back} className="btn btn-ghost btn-sm" data-testid="verif-return-link"><ArrowLeft size={14} aria-hidden="true" /> Reprendre</Link>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }} data-testid="verif-email">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mail size={20} aria-hidden="true" />
            <div>
              <div className="section-title" style={{ margin: 0 }}>{VERIFICATION_TYPES.email.label}</div>
              <div style={{ fontSize: 13, color: 'var(--ink2)' }}>{user?.email}</div>
            </div>
          </div>
          <Badge state={email} />
        </div>
        {email !== 'VERIFIED' && (
          <form onSubmit={confirm} style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <label htmlFor="verif-code" style={{ fontSize: 13, width: '100%' }}>
              Saisissez le code à 6 chiffres envoyé à votre adresse (valable 10 minutes).
            </label>
            <input id="verif-code" className="field-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
              style={{ maxWidth: 140, letterSpacing: 4 }} data-testid="verif-email-code" />
            <button type="submit" className="btn btn-primary btn-sm" disabled={code.length !== 6 || busy === 'confirm'} data-testid="verif-email-confirm">
              Vérifier
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={sendCode} disabled={busy === 'send'} data-testid="verif-email-send">
              Recevoir un nouveau code
            </button>
          </form>
        )}
      </div>

      <PhoneCard user={user} onVerified={checkAuth} badge={<Badge state={verificationStatus(user, 'phone')} />} />

      <div className="card" style={{ marginBottom: 16 }} data-testid="verif-identity">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IdCard size={20} aria-hidden="true" />
            <div className="section-title" style={{ margin: 0 }}>{VERIFICATION_TYPES.identity.label}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Badge state={verificationStatus(user, 'identity')} />
            {verificationStatus(user, 'identity') !== 'VERIFIED' && (
              <Link to={stepLink('identity', back)} className="btn btn-primary btn-sm" data-testid="verif-identity-link">{VERIFICATION_TYPES.identity.cta}</Link>
            )}
          </div>
        </div>
      </div>

      {user?.profile_type === 'PROFESSIONAL' && (
        <div className="card" data-testid="verif-company">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Building2 size={20} aria-hidden="true" />
              <div className="section-title" style={{ margin: 0 }}>{VERIFICATION_TYPES.company.label}</div>
            </div>
            <Badge state={types.company || 'PENDING'} />
          </div>
        </div>
      )}
    </div>
  );
}
